import { useNotifications } from '@/hooks/useNotifications';
import { DynamicIcon } from '@/components/shell/DynamicIcon';
import { cn } from '@/lib/utils';

const iconMap = {
  success: 'circle-check',
  error: 'circle-alert',
  warning: 'triangle-alert',
  info: 'info',
} as const;

// IC-601: the alpha-blended `bg-<color>/10` backgrounds were never opaque —
// legible enough over the page's own white/dark background, illegible over
// anything else behind the toast (e.g. the green top bar, in a screenshot
// filed against this). `bg-card` is a real, theme-aware opaque surface;
// the border/icon/text keep the semantic color.
const colorMap = {
  success: 'border-success bg-card text-success',
  error: 'border-destructive bg-card text-destructive',
  warning: 'border-warning bg-card text-warning',
  info: 'border-brand-blue bg-card text-brand-blue',
} as const;

/** Polite for success and info, assertive for error and warning (WCAG 4.1.3). */
const roleMap = {
  success: 'status',
  info: 'status',
  error: 'alert',
  warning: 'alert',
} as const;

export function NotificationToast() {
  const { notifications, removeNotification } = useNotifications();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed right-4 top-4 z-50 flex flex-col gap-2">
      {notifications.map((n) => (
        <div
          key={n.id}
          role={roleMap[n.type]}
          className={cn(
            'flex items-start gap-2 rounded border-l-4 p-3 shadow-lg',
            colorMap[n.type],
          )}
        >
          <DynamicIcon name={iconMap[n.type]} className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs font-medium">{n.title}</p>
            {n.message && <p className="text-xs opacity-80">{n.message}</p>}
          </div>
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => removeNotification(n.id)}
            className="flex-shrink-0 opacity-60 hover:opacity-100"
          >
            <DynamicIcon name="x" className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
